import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Tan {
  kiado: string; alany: string; san: string; ervenyes_tol: string; ervenyes_ig: string; lanc_db: number;
  feltoltve: string; feltoltotte: string; lejart: boolean; hamarosan_lejar: boolean;
}
interface Kulcs {
  id: string; kornyezet: string; szerep: string; domain: string; subject: string;
  kornyezet_nev: string; szerep_nev: string; letrehozva: string; letrehozta: string; tanusitvany: Tan | null;
}
interface Kornyezet { kulcs: string; nev: string; kell: string[] }

/**
 *  ADMIN › DÁP-CSATLAKOZÁS – a DÁP eAzonosítás csatlakozási kérelmének kulcsai (backend/dap-csatlakozas.js).
 *
 *  Az intézmény a SZEÜSZ-ön maga regisztrál, környezetenként (sandbox, teszt, éles). A kérelemhez
 *  tanúsítványkérelmet csatol: sandboxban kliens- és aláíró (bélyegző) kérelmet, teszt- és éles
 *  környezetben csak aláírót. A kulcspárt a szerver generálja és őrzi (nem a saját gépen –
 *  ott elveszhet); innen tölthető le a kérelem, és ide tölthető vissza a kiállított tanúsítvány.
 */
@Component({
  selector: 'app-admin-dap-csatlakozas',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './dap-csatlakozas.component.html',
  styleUrls: ['./dap-csatlakozas.component.scss'],
})
export class AdminDapCsatlakozasComponent implements OnInit {
  betolt = false;
  keszul = false;
  fut: Record<string, boolean> = {};
  kulcsok: Kulcs[] = [];
  kornyezetek: Kornyezet[] = [];
  kornyezet = 'sandbox';
  adat: any = { domain: '', c: 'HU', st: '', l: '', o: '', ou: 'Sulinform' };
  rendszerNev = '';

  constructor(private http: HttpClient, private data: DataService, private alert: AlertController) {}

  ngOnInit() { this.toltes(true); }

  get aktiv(): Kornyezet | undefined { return this.kornyezetek.find((k) => k.kulcs === this.kornyezet); }
  /** A környezet kulcsai, szerepenként a legfrissebb elöl. */
  get lista(): Kulcs[] { return this.kulcsok.filter((k) => k.kornyezet === this.kornyezet); }
  /** A kitöltési segédhez: szerepenként a legfrissebb kérelem. */
  legfrissebb(szerep: string): Kulcs | undefined { return this.lista.find((k) => k.szerep === szerep); }
  szerepNev(s: string) { return s === 'kliens' ? 'Klienstanúsítvány-kérelem' : 'Aláírótanúsítvány-kérelem'; }

  async toltes(elso = false) {
    this.betolt = true;
    try {
      const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
      const r: any = await firstValueFrom(this.http.get('/api/dap/csatlakozas', { params: { intezmeny: String(sajat?.intezmenyId || '') } }));
      this.kulcsok = r.kulcsok || [];
      this.kornyezetek = r.kornyezetek || [];
      if (elso) {
        const utolso = this.kulcsok[0];
        //  Ha már készült kérelem, annak adatai az alap (ugyanazzal a domainnel megy a többi is).
        this.adat = { ...this.adat, ...(r.alap || {}), ...(utolso ? { domain: utolso.domain } : {}) };
        this.rendszerNev = `Sulinform – ${this.adat.o || 'iskolai tanulmányi rendszer'}`;
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a DÁP-csatlakozást.', 'error');
    } finally { this.betolt = false; }
  }

  /** A környezethez szükséges kérelmek (mindegyikhez új kulcspár). */
  async kerelmekKeszitese() {
    const kell = this.aktiv?.kell || [];
    const megvan = kell.filter((s) => this.legfrissebb(s));
    if (megvan.length) {
      const a = await this.alert.create({
        header: 'Új kérelmek',
        message: `Ehhez a környezethez már van ${megvan.map((s) => this.szerepNev(s).toLowerCase()).join(' és ')}. Új kulcspárral újakat készítsek? A régiek megmaradnak.`,
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Igen, újak', role: 'ok' }],
      });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'ok') return;
    }
    this.keszul = true;
    try {
      for (const szerep of kell) {
        await firstValueFrom(this.http.post('/api/dap/kerelem', { ...this.adat, kornyezet: this.kornyezet, szerep }));
      }
      this.data.sendToast('A kérelmek elkészültek – töltsd le és csatold őket a SZEÜSZ-űrlaphoz.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült elkészíteni a kérelmet.', 'error');
    } finally { this.keszul = false; }
  }

  private mentes(blob: Blob, nev: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = nev;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async kerelemLetoltes(k: Kulcs) {
    try {
      const blob = await firstValueFrom(this.http.get(`/api/dap/kerelem/${k.id}`, { responseType: 'blob' }));
      this.mentes(blob, `certificaterequest-${k.szerep === 'kliens' ? 'clientauth' : 'seal'}-${k.kornyezet}.p10`);
    } catch (e: any) { this.data.sendToast('Nem sikerült letölteni a kérelmet.', 'error'); }
  }

  async masol(szoveg: string) {
    try { await navigator.clipboard.writeText(szoveg); this.data.sendToast('Vágólapra másolva.', 'success'); }
    catch (_) { this.data.sendToast('A másolás nem sikerült – jelöld ki kézzel.', 'warning'); }
  }

  /** A kiállított tanúsítvány feltöltése (PEM, DER/.cer, .p7b). */
  async tanusitvanyFeltoltes(k: Kulcs, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    this.fut[k.id] = true;
    try {
      const b64 = await new Promise<string>((ok, no) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ''));
        r.onerror = () => no(r.error);
        r.readAsDataURL(f);
      });
      const v: any = await firstValueFrom(this.http.post(`/api/dap/tanusitvany/${k.id}`, { fajl: b64 }));
      if (v?.figyelmeztetes?.length) this.data.sendToast(`Feltöltve, de: ${v.figyelmeztetes.join(' ')}`, 'warning');
      else this.data.sendToast('A tanúsítvány feltöltve, a kulcshoz illik.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült feltölteni a tanúsítványt.', 'error');
    } finally { this.fut[k.id] = false; }
  }

  /** Klienstanúsítvány a böngészőbe: jelszavas .p12. */
  async p12(k: Kulcs) {
    const a = await this.alert.create({
      header: 'Klienstanúsítvány letöltése',
      message: 'A .p12 a privát kulcsot is tartalmazza. Adj meg egy jelszót (legalább 8 karakter) – a böngészőbe importáláskor ezt kéri.',
      inputs: [{ name: 'jelszo', type: 'password', placeholder: 'Jelszó' }, { name: 'jelszo2', type: 'password', placeholder: 'Jelszó újra' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Letöltés', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    const j = String(data?.values?.jelszo || '');
    if (j.length < 8) { this.data.sendToast('A jelszó legalább 8 karakter.', 'warning'); return; }
    if (j !== data?.values?.jelszo2) { this.data.sendToast('A két jelszó nem egyezik.', 'warning'); return; }
    this.fut[k.id] = true;
    try {
      const blob = await firstValueFrom(this.http.post(`/api/dap/p12/${k.id}`, { jelszo: j }, { responseType: 'blob' }));
      this.mentes(blob, `dap-kliens-${k.kornyezet}.p12`);
    } catch (e: any) {
      let uzenet = 'Nem sikerült letölteni.';
      try { uzenet = JSON.parse(await (e?.error as Blob).text())?.error || uzenet; } catch (_) { /* nem JSON */ }
      this.data.sendToast(uzenet, 'error');
    } finally { this.fut[k.id] = false; }
  }

  async torles(k: Kulcs) {
    const a = await this.alert.create({
      header: 'Kérelem törlése',
      message: `Törlöd ezt a ${k.szerep_nev.toLowerCase()}-kérelmet és a kulcsát? Csak akkor tedd, ha nem adtad be (vagy visszavontad).`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'ok' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    try {
      await firstValueFrom(this.http.delete(`/api/dap/kerelem/${k.id}`));
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült törölni.', 'error'); }
  }
}
